import React, { useState, useEffect, useRef, useCallback } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { EmblaCarouselType, EmblaEventType } from 'embla-carousel'

import Link from '@atoms/Link'
import Instagram from '@icons/Instagram'

import * as style from './style.module.sass'

type InstagramMediaType = 'IMAGE' | 'VIDEO' | 'CAROUSEL_ALBUM'

export type InstagramPost = {
  mediaUrl: string
  permalink: string
  mediaType: InstagramMediaType
  caption?: string
}

export type InstagramCarouselProps = {
  posts: InstagramPost[]
}

const selectedSize = 400
const normalSize = 180
const scaleRatio = selectedSize / normalSize

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max)

const InstagramCarousel: React.FC<InstagramCarouselProps> = ({ posts }) => {
  const captions = posts.map(({ caption }) => caption)

  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      // 境界で表示崩れが発生するため.carouselContainerのwidthを200%にしており、そのwidthと連動させる
      align: (carouselSize, snapSize) => carouselSize / 4 + snapSize / 2,
    },
    [],
  )

  const tweenNodes = useRef<HTMLElement[]>([])
  const setTweenNodes = useCallback((emblaApi: EmblaCarouselType): void => {
    tweenNodes.current = emblaApi.slideNodes().map(slideNode => {
      return slideNode.querySelector('.' + style.item) as HTMLElement
    })
  }, [])

  const tweenScale = useCallback(
    (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = emblaApi.internalEngine()
      const scrollProgress = emblaApi.scrollProgress()
      const slidesInView = emblaApi.slidesInView()
      const isScrollEvent = eventName === 'scroll'

      const scrollSnapList = emblaApi.scrollSnapList()
      scrollSnapList.forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress
        const slidesInSnap = engine.slideRegistry[snapIndex]

        slidesInSnap.forEach(slideIndex => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach(loopItem => {
              const target = loopItem.target()

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target)

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress)
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress)
                }
              }
            })
          }

          const enlargedDiff = diffToTarget * scrollSnapList.length
          const tweenValue = 1 - Math.abs(enlargedDiff * 0.6)
          const scale =
            numberWithinRange(tweenValue, 1 / scaleRatio, 1) * scaleRatio
          const tweenNode = tweenNodes.current[slideIndex]

          const translateX =
            (Math.sign(enlargedDiff) *
              numberWithinRange(Math.abs(enlargedDiff * 1.1), 0, 1) *
              (selectedSize - normalSize)) /
            2
          const translateYValue = 1 - Math.abs(enlargedDiff * 1.2)
          const translateY =
            (numberWithinRange(translateYValue, 0, 1) *
              ((selectedSize - normalSize) / 2 - 81)) /
            scaleRatio
          tweenNode.style.transform = `scale(${scale}) translate(${translateX}px, ${translateY}px)`
        })
      })
    },
    [],
  )

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    setTweenNodes(emblaApi)
    tweenScale(emblaApi)
    onSelect(emblaApi)

    emblaApi.on('reInit', setTweenNodes)
    emblaApi.on('reInit', tweenScale)
    emblaApi.on('reInit', onSelect)
    emblaApi.on('select', onSelect).on('scroll', tweenScale)
  }, [emblaApi, onSelect, setTweenNodes, tweenScale])

  const media = (
    mediaUrl: InstagramPost['mediaUrl'],
    mediaType: InstagramPost['mediaType'],
    caption: InstagramPost['caption'],
  ) => {
    return (
      <div className={style.mediaContainer}>
        {mediaType === 'VIDEO' ? (
          <video autoPlay muted loop playsInline>
            <source src={mediaUrl} type="video/mp4" />
          </video>
        ) : (
          <img src={mediaUrl} alt={caption} />
        )}
      </div>
    )
  }

  return (
    <div className={style.container}>
      <div ref={emblaRef} className={style.carouselContainer}>
        <div className={style.carousel}>
          {posts.map(
            ({ mediaUrl, permalink, mediaType, caption }, index: number) => (
              <div className={style.itemContainer} key={index}>
                <div
                  className={style.item}
                  onClick={() => {
                    emblaApi?.scrollTo(index)
                  }}
                >
                  {index === selectedIndex ? (
                    <Link to={permalink}>
                      {media(mediaUrl, mediaType, caption)}
                    </Link>
                  ) : (
                    media(mediaUrl, mediaType, caption)
                  )}
                </div>
                {index === selectedIndex && (
                  <Link
                    to="https://www.instagram.com/ftn_artisan/"
                    className={style.instagram}
                  >
                    <Instagram />
                    @FTN_Artisan
                  </Link>
                )}
              </div>
            ),
          )}
        </div>
        <div className={style.captionContainer}>
          <div className={style.caption}>{captions[selectedIndex]}</div>
        </div>
      </div>
    </div>
  )
}

export default InstagramCarousel
